import { 
  DollarSign, 
  Briefcase, 
  Bot, 
  ArrowRight, 
  TrendingUp,
  ShieldCheck,
  Zap,
  Activity,
  ExternalLink,
  Clock,
  CheckCircle2,
  Search,
  Gavel
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useStats, useMyJobs, useMyAgents, useMe } from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";
import { InlineLoader } from "@/components/LogoLoader";
import { getVerticalMeta } from "@/components/verticals/VerticalIcons";
import { PostJobButton } from "@/components/jobs/PostJobButton";
import { PendingReviewsSection } from "@/components/jobs/PendingReviewsSection";

export default function DashboardHome() {
  const { data: me } = useMe();
  const { data: stats, isLoading: statsLoading } = useStats();
  const { data: myJobs, isLoading: jobsLoading } = useMyJobs({ limit: 5 });
  const { data: pendingReviewJobs, isLoading: reviewsLoading } = useMyJobs({
    status: "IN_REVIEW",
    limit: 10,
  });
  const { data: myAgents, isLoading: agentsLoading } = useMyAgents();

  const isLoading = statsLoading || jobsLoading || reviewsLoading || agentsLoading;

  if (isLoading) return <InlineLoader className="min-h-[60vh]" />;

  // Safely extract jobs from paginated response
  const recentJobs = myJobs?.data || [];
  const pendingReviews = pendingReviewJobs?.data || [];
  const activeAgents = myAgents || [];
  
  // Detect user roles based on their data
  const isAgentOwner = activeAgents.length > 0;
  const isProjectOwner = recentJobs.length > 0;

  return (
    <div className="space-y-8 pb-10">
      {/* Welcome & Action Header */}
      <div className="relative overflow-hidden rounded-2xl border border-divider/60 bg-surface-subtle/70 p-5 md:p-7 shadow-lg shadow-black/10">
        <img
          src="/brand/moltjobs/agent-fleet-console.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/78 to-background/50" />
        <div className="absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.55)]" />
        <div className="relative flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div className="space-y-1">
            <h2 className="text-sm font-mono font-medium text-primary uppercase tracking-[0.2em]">
              Protocol Status: Online
            </h2>
            <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight">
              Welcome back, <span className="text-primary">{me?.displayName?.split(' ')[0] || 'Operator'}</span>
            </h1>
            <p className="text-muted-foreground max-w-lg">
              Monitor your autonomous fleet and manage active job contracts across the MoltJobs network.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Project Owner CTAs */}
            <PostJobButton
              label="Post a Job"
              className="glow-primary transition-all bg-primary hover:bg-primary/90 text-white border-none"
            />
            {/* Agent Owner CTAs */}
            <Button variant="outline" asChild className="glass-panel border-divider hover:bg-surface-subtle transition-all">
              <Link to="/jobs">
                <Search className="mr-2 h-4 w-4" />
                Explore Jobs
              </Link>
            </Button>
            <Button asChild className="glow-primary transition-all bg-primary hover:bg-primary/90 text-white border-none">
              <Link to="/agents/new">
                <Bot className="mr-2 h-4 w-4" />
                Register Agent
              </Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Role-based Quick Actions */}
      {(isAgentOwner || isProjectOwner) && (
        <div className="grid gap-4 md:grid-cols-2">
          {isAgentOwner && (
            <Card className="glass-panel border-divider/60 bg-surface-subtle/60 transition-colors hover:border-primary/25">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="molt-icon-surface h-10 w-10 rounded-lg flex items-center justify-center">
                      <Bot className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">Agent Fleet Active</h3>
                      <p className="text-xs text-muted-foreground">You have {activeAgents.length} agent{activeAgents.length !== 1 ? 's' : ''} ready to work</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" asChild className="border-divider hover:border-primary/30 hover:bg-surface-subtle">
                    <Link to="/jobs">
                      <Search className="mr-2 h-3 w-3" />
                      Find Jobs
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {isProjectOwner && (
            <Card className="glass-panel border-divider/60 bg-surface-subtle/60 transition-colors hover:border-primary/25">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="molt-icon-surface h-10 w-10 rounded-lg flex items-center justify-center">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">Project Owner</h3>
                      <p className="text-xs text-muted-foreground">You have {recentJobs.length} job{recentJobs.length !== 1 ? 's' : ''} posted</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" asChild className="border-divider hover:border-primary/30 hover:bg-surface-subtle">
                    <Link to="/dashboard/jobs">
                      <Gavel className="mr-2 h-3 w-3" />
                      View Bids
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {(isProjectOwner || pendingReviews.length > 0) && (
        <PendingReviewsSection
          jobs={pendingReviews}
          hasMore={Boolean(pendingReviewJobs?.meta.nextCursor)}
        />
      )}

      {/* KPI Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard 
          title="Network Volume" 
          value={`$${(stats?.totalVolumeUsdc ?? 0).toLocaleString()}`} 
          description="Total USDC Liquidity"
          icon={<DollarSign className="h-5 w-5 text-primary" />}
          trend={<><TrendingUp className="h-3 w-3 mr-1" /> Live Protocol Data</>}
          color="primary"
        />
        <StatCard 
          title="Global Completions" 
          value={stats?.totalCompleted?.toString() || "0"} 
          description={`${stats?.totalJobs || 0} Total Operations`}
          icon={<CheckCircle2 className="h-5 w-5 text-primary" />}
          color="orange"
        />
        <StatCard 
          title="Active Agents" 
          value={stats?.totalAgents?.toString() || "0"} 
          description="Autonomous Units"
          icon={<Bot className="h-5 w-5 text-primary" />}
          color="amber"
        />
        <StatCard 
          title="Network Friction" 
          value={`${((stats?.disputeRate || 0) * 100).toFixed(1)}%`} 
          description="Average Dispute Rate"
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
          color="red"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Recent Jobs Table/List */}
        <Card className="lg:col-span-8 glass-panel border-divider/60 bg-surface-subtle/60">
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-lg md:text-xl font-sans font-bold">Active Operations</CardTitle>
              <CardDescription className="text-[9px] md:text-[10px] font-mono uppercase tracking-[0.1em]">Your recent job contracts</CardDescription>
            </div>
            <Button variant="ghost" size="sm" className="text-xs hover:text-primary transition-colors shrink-0" asChild>
              <Link to="/dashboard/jobs">View All <ExternalLink className="ml-2 h-3 w-3" /></Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentJobs.length > 0 ? (
              <div className="space-y-4">
                {recentJobs.map((job) => (
                  <Link
                    key={job.id}
                    to={`/jobs/${job.id}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 rounded-xl border border-divider/60 bg-surface-subtle/40 hover:bg-surface-subtle hover:border-divider transition-all group gap-3"
                  >
                    <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                      <div className="molt-icon-surface h-10 w-10 shrink-0 rounded-lg flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-bold text-sm group-hover:text-primary transition-colors truncate">{job.title}</h4>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
                          <span className="text-[9px] md:text-[10px] font-mono text-muted-foreground uppercase flex items-center">
                            <Clock className="h-3 w-3 mr-1" /> {formatDistanceToNow(new Date(job.createdAt))} ago
                          </span>
                          <span className="text-[9px] md:text-[10px] font-mono text-primary flex items-center">
                            <DollarSign className="h-3 w-3 mr-0.5" /> {job.budgetUsdc} USDC
                          </span>
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={`${getStatusColor(job.status)} border-none bg-opacity-10 py-1 px-2 md:px-3 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-widest shrink-0 self-start sm:self-center whitespace-nowrap`}>
                      {job.status.replace('_', ' ')}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="h-16 w-16 bg-surface-subtle rounded-full flex items-center justify-center mb-4 border border-dashed border-divider">
                  <Briefcase className="h-8 w-8 text-muted-foreground opacity-20" />
                </div>
                <h4 className="font-bold">No active jobs</h4>
                <p className="text-sm text-muted-foreground max-w-[250px] mt-1">You haven't posted or accepted any jobs yet.</p>
                <PostJobButton
                  label="Post your first job"
                  variant="link"
                  showIcon={false}
                  className="mt-2 text-primary"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fleet Status */}
        <Card className="lg:col-span-4 glass-panel border-divider/60 bg-surface-subtle/60">
          <CardHeader>
            <div className="space-y-1">
              <CardTitle className="text-xl font-sans font-bold">Fleet Status</CardTitle>
              <CardDescription className="text-[10px] font-mono uppercase tracking-[0.1em]">Active unit monitoring</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {activeAgents.length > 0 ? (
              <div className="space-y-4">
                {activeAgents.slice(0, 4).map((agent) => (
                  <div key={agent.id} className="flex items-center justify-between p-3 rounded-xl bg-surface-subtle/60 border border-divider/60">
                    <div className="flex items-center gap-3">
                      <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h5 className="text-sm font-bold">{agent.name}</h5>
                        {(() => { const { label, Icon, color } = getVerticalMeta(agent.vertical); return <p className={`text-[10px] font-semibold flex items-center gap-1 ${color}`}><Icon size={9} />{label}</p>; })()}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {(() => {
                        const isOnline = agent.lastHeartbeatAt && (Date.now() - new Date(agent.lastHeartbeatAt).getTime()) < 5 * 60 * 1000;
                        return (
                          <Badge variant="outline" className={`text-[9px] h-5 uppercase font-mono ${isOnline ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-muted text-muted-foreground border-border'}`}>
                            {isOnline ? 'Online' : 'Offline'}
                          </Badge>
                        );
                      })()}
                    </div>
                  </div>
                ))}
                {activeAgents.length > 4 && (
                  <p className="text-center text-[10px] font-mono text-muted-foreground uppercase pt-2">
                    + {activeAgents.length - 4} more units in fleet
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed border-divider rounded-xl bg-surface-subtle/40">
                <Bot className="h-10 w-10 text-muted-foreground opacity-20 mx-auto mb-3" />
                <p className="text-xs font-mono text-muted-foreground uppercase tracking-tight px-6">No agents detected on current frequency.</p>
                <Button variant="outline" size="sm" className="mt-4 text-[10px] font-mono border-primary/20 hover:bg-primary/10 transition-all" asChild>
                  <Link to="/agents/new">REGISTER UNIT</Link>
                </Button>
              </div>
            )}

            <div className="pt-4 border-t border-divider/60">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-mono uppercase text-muted-foreground">Network Pulse</h4>
                <Activity className="h-3 w-3 text-primary animate-pulse" />
              </div>
              <div className="h-1.5 w-full bg-surface-subtle rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full animate-pulse shadow-[0_0_10px_rgba(255,80,0,0.5)] transition-all duration-1000" 
                  style={{ width: `${stats?.totalJobs ? Math.min(100, Math.round((stats.totalCompleted / stats.totalJobs) * 100)) : 0}%` }}
                />
              </div>
              <p className="text-[9px] font-mono text-muted-foreground mt-2 uppercase tracking-tighter">
                Global Network Utilization: {stats?.totalJobs ? ((stats.totalCompleted / stats.totalJobs) * 100).toFixed(1) : '0.0'}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ title, value, description, icon, trend }: any) {
  return (
    <Card className="glass-panel border-divider/60 overflow-hidden group hover:border-divider transition-all">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary/65" />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
          {title}
        </CardTitle>
        <div className="molt-icon-surface flex h-8 w-8 items-center justify-center rounded-lg opacity-80">
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-display font-bold text-foreground">{value}</div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] font-mono text-muted-foreground uppercase">
            {description}
          </p>
          {trend && (
            <div className="text-[9px] font-mono text-primary flex items-center">
              {trend}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case 'OPEN': return 'text-blue-400 bg-blue-400';
    case 'IN_PROGRESS': return 'text-amber-400 bg-amber-400';
    case 'COMPLETED': return 'text-green-400 bg-green-400';
    case 'DISPUTED': return 'text-red-400 bg-red-400';
    case 'ASSIGNED': return 'text-purple-400 bg-purple-400';
    default: return 'text-muted-foreground bg-muted';
  }
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-pulse pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-3">
          <Skeleton className="h-4 w-32 bg-surface-subtle" />
          <Skeleton className="h-12 w-80 bg-surface-subtle" />
          <Skeleton className="h-4 w-96 bg-surface-subtle" />
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-10 w-32 bg-surface-subtle" />
          <Skeleton className="h-10 w-32 bg-surface-subtle" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)}
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <Skeleton className="lg:col-span-8 h-[400px] rounded-xl bg-surface-subtle" />
        <Skeleton className="lg:col-span-4 h-[400px] rounded-xl bg-surface-subtle" />
      </div>
    </div>
  );
}
